import React from 'react';

const ACTION_SUGGESTIONS = [
  'Open investment account',
  'Transfer existing holdings',
  'Establish pension account',
  'Cancel existing arrangements',
  'Set up regular contributions',
];

const DEFAULT_AUTHORISATIONS = [
  'I/We authorise the adviser to proceed with the recommended strategy',
  'I/We authorise the transfer of funds as described',
  'I/We acknowledge the risks outlined in this document',
  'I/We confirm the information provided is accurate and complete',
];

const DEFAULT_AUTHORITY_TEXT =
  'I/We have read and understood this Statement of Advice and the recommendations contained within. I/We acknowledge that the adviser has explained the basis of the advice, the risks involved, and the costs associated with implementing the recommendations. I/We authorise the adviser to proceed with the implementation of the recommended strategy as outlined in this document.';

const StepActionsAuthority = ({ formData, updateFormData, prevStep, nextStep, disabled }) => {
  const data = formData.actionsAuthority;

  const handleChange = (field, value) => {
    updateFormData('actionsAuthority', { [field]: value });
  };

  // --- Action Items ---
  const addActionItem = (description = '') => {
    const items = [...(data.actionItems || []), { description, responsible: '' }];
    handleChange('actionItems', items);
  };

  const updateActionItem = (index, field, value) => {
    const items = [...(data.actionItems || [])];
    items[index] = { ...items[index], [field]: value };
    handleChange('actionItems', items);
  };

  const removeActionItem = (index) => {
    const items = (data.actionItems || []).filter((_, i) => i !== index);
    handleChange('actionItems', items);
  };

  const addActionSuggestion = (description) => {
    const existing = (data.actionItems || []).some(
      (item) => item.description.toLowerCase() === description.toLowerCase()
    );
    if (!existing) {
      addActionItem(description);
    }
  };

  // --- Specific Authorisations ---
  const toggleAuthorisation = (index) => {
    const auths = [...(data.specificAuthorisations || [])];
    auths[index] = { ...auths[index], checked: !auths[index].checked };
    handleChange('specificAuthorisations', auths);
  };

  const addCustomAuthorisation = () => {
    const auths = [...(data.specificAuthorisations || []), { action: '', checked: false }];
    handleChange('specificAuthorisations', auths);
  };

  const updateAuthorisationText = (index, value) => {
    const auths = [...(data.specificAuthorisations || [])];
    auths[index] = { ...auths[index], action: value };
    handleChange('specificAuthorisations', auths);
  };

  const removeAuthorisation = (index) => {
    const auths = (data.specificAuthorisations || []).filter((_, i) => i !== index);
    handleChange('specificAuthorisations', auths);
  };

  // Initialise defaults if empty
  React.useEffect(() => {
    if (!data.specificAuthorisations || data.specificAuthorisations.length === 0) {
      handleChange(
        'specificAuthorisations',
        DEFAULT_AUTHORISATIONS.map((action) => ({ action, checked: false }))
      );
    }
    if (!data.authorityText) {
      handleChange('authorityText', DEFAULT_AUTHORITY_TEXT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <fieldset disabled={disabled}>
      <h4 className="soa-step-title">Actions &amp; Authority to Proceed</h4>
      <p className="soa-step-subtitle">
        Outline implementation steps and obtain client authority to proceed with the advice
      </p>

      {/* Implementation Actions */}
      <div className="soa-section-banner">
        <i className="bi bi-list-check"></i>
        Implementation Actions
      </div>

      <div className="mb-2">
        <span className="small text-muted me-2">Quick add:</span>
        {ACTION_SUGGESTIONS.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            className="btn btn-sm me-1 mb-1"
            style={{
              backgroundColor: 'var(--info-color)',
              color: '#fff',
              fontSize: '0.78rem',
            }}
            onClick={() => addActionSuggestion(suggestion)}
            disabled={disabled}
          >
            <i className="bi bi-plus me-1"></i>{suggestion}
          </button>
        ))}
      </div>

      {(data.actionItems || []).map((item, idx) => (
        <div key={idx} className="soa-item-row">
          <span
            className="fw-bold d-flex align-items-center justify-content-center"
            style={{
              minWidth: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: 'var(--primary-color)',
              color: '#fff',
              fontSize: '0.8rem',
            }}
          >
            {idx + 1}
          </span>
          <input
            type="text"
            className="form-control flex-grow-1"
            value={item.description}
            onChange={(e) => updateActionItem(idx, 'description', e.target.value)}
            placeholder="Action description"
          />
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: '180px' }}
            value={item.responsible}
            onChange={(e) => updateActionItem(idx, 'responsible', e.target.value)}
            placeholder="Responsible party"
          />
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={() => removeActionItem(idx)}
            disabled={disabled}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      ))}

      <button
        className="btn btn-outline-primary btn-sm mb-3"
        onClick={() => addActionItem()}
        disabled={disabled}
      >
        <i className="bi bi-plus-lg me-1"></i>Add Action Item
      </button>

      {/* Specific Authorisations */}
      <div className="soa-section-banner">
        <i className="bi bi-check2-square"></i>
        Specific Authorisations
      </div>

      {(data.specificAuthorisations || []).map((auth, idx) => (
        <div key={idx} className="soa-item-row" style={{ alignItems: 'center' }}>
          <div className="form-check flex-grow-1 d-flex align-items-center gap-2">
            <input
              type="checkbox"
              className="form-check-input"
              id={`auth-${idx}`}
              checked={auth.checked || false}
              onChange={() => toggleAuthorisation(idx)}
              style={{ marginTop: 0 }}
            />
            {DEFAULT_AUTHORISATIONS.includes(auth.action) ? (
              <label className="form-check-label" htmlFor={`auth-${idx}`}>
                {auth.action}
              </label>
            ) : (
              <input
                type="text"
                className="form-control form-control-sm flex-grow-1"
                value={auth.action}
                onChange={(e) => updateAuthorisationText(idx, e.target.value)}
                placeholder="Custom authorisation..."
              />
            )}
          </div>
          {!DEFAULT_AUTHORISATIONS.includes(auth.action) && (
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => removeAuthorisation(idx)}
              disabled={disabled}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>
      ))}

      <button
        className="btn btn-outline-primary btn-sm mb-3"
        onClick={addCustomAuthorisation}
        disabled={disabled}
      >
        <i className="bi bi-plus-lg me-1"></i>Add Custom Authorisation
      </button>

      {/* Authority Statement */}
      <div className="soa-section-banner">
        <i className="bi bi-pen"></i>
        Authority Statement
      </div>

      <div className="soa-form-group">
        <label>Authority to Proceed Statement</label>
        <textarea
          className="form-control"
          value={data.authorityText}
          onChange={(e) => handleChange('authorityText', e.target.value)}
          rows={5}
        />
      </div>

      <div className="soa-step-nav">
        <button className="btn btn-outline-secondary" onClick={prevStep}>
          <i className="bi bi-arrow-left me-1"></i>Back
        </button>
        <button className="btn btn-primary" onClick={nextStep}>
          Next: Warnings & Disclaimers <i className="bi bi-arrow-right ms-1"></i>
        </button>
      </div>
    </fieldset>
  );
};

export default StepActionsAuthority;
